import React, { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Check, FolderPlus, Loader2 } from "lucide-react";
import { toast } from "sonner";

import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

import { api, formatApiError } from "@/lib/api";
import { FolderIcon, folderColorClasses } from "@/components/folderTheme";
import CreateFolderModal from "@/components/CreateFolderModal";

/**
 * Multi-select dropdown for adding/removing a run OR workflow from folders.
 *
 * Pass exactly one of `runId` or `workflowId` — the component switches
 * its API endpoints based on which was provided.  When `workflowId` is
 * set, the per-folder toggle hits `/folders/{id}/workflow-items` and
 * the membership query hits `/workflows/{id}/folders`.
 */
export default function AddToFolderDropdown({ runId, workflowId, children }) {
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [search, setSearch] = useState("");
  // R3-Phase-1 — "Move (replace memberships)" mode.  Off by default.
  const [mode, setMode] = useState("add"); // "add" | "move"
  // Track in-flight folder IDs so we can show a spinner inline.
  const [busy, setBusy] = useState(() => new Set());

  // Sanity: exactly one of runId / workflowId.
  const targetKind = workflowId ? "workflow" : "run";
  const targetId = workflowId || runId;

  // All folders the user can see (mine + org).
  const foldersQuery = useQuery({
    queryKey: ["folders", "all"],
    queryFn: async () =>
      (await api.get("/folders?visibility=all&page_size=200")).data,
    enabled: open,
  });

  // Current memberships for this target.
  const membershipQuery = useQuery({
    queryKey: [
      targetKind === "workflow" ? "workflow-folders" : "run-folders",
      targetId,
    ],
    queryFn: async () => {
      const path =
        targetKind === "workflow"
          ? `/workflows/${targetId}/folders`
          : `/demo-runs/${targetId}/folders`;
      return (await api.get(path)).data;
    },
    enabled: open && !!targetId,
  });

  const folders = useMemo(() => foldersQuery.data?.items || [], [foldersQuery.data]);
  const memberFolderIds = useMemo(
    () => new Set((membershipQuery.data?.folders || []).map((f) => f.folder_id)),
    [membershipQuery.data],
  );

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return folders;
    return folders.filter((f) => f.name.toLowerCase().includes(q));
  }, [folders, search]);

  const toggleMutation = useMutation({
    mutationFn: async ({ folderId, add }) => {
      // R3-Phase-1 — Move mode short-circuits to the atomic
      // /move-{workflows|runs} endpoint and ignores the per-row
      // add/remove toggle.  Add mode keeps the existing
      // multi-membership semantics.
      if (mode === "move") {
        if (targetKind === "workflow") {
          return (
            await api.post(`/folders/${folderId}/move-workflows`, {
              workflow_ids: [targetId],
            })
          ).data;
        }
        return (
          await api.post(`/folders/${folderId}/move-runs`, {
            run_ids: [targetId],
          })
        ).data;
      }
      if (targetKind === "workflow") {
        if (add) {
          return (
            await api.post(`/folders/${folderId}/workflow-items`, {
              workflow_id: targetId,
            })
          ).data;
        }
        await api.delete(`/folders/${folderId}/workflow-items/${targetId}`);
        return { ok: true };
      }
      if (add) {
        return (
          await api.post(`/folders/${folderId}/items`, { run_id: targetId })
        ).data;
      }
      await api.delete(`/folders/${folderId}/items/${targetId}`);
      return { ok: true };
    },
    onMutate: ({ folderId }) => {
      setBusy((prev) => new Set([...prev, folderId]));
    },
    onSettled: (_data, _err, { folderId }) => {
      setBusy((prev) => {
        const next = new Set(prev);
        next.delete(folderId);
        return next;
      });
    },
    onSuccess: (_data, { add, folderName }) => {
      if (mode === "move") {
        toast.success(`Moved to "${folderName}"`);
      } else {
        toast.success(add ? `Added to "${folderName}"` : `Removed from "${folderName}"`);
      }
      queryClient.invalidateQueries({
        queryKey:
          targetKind === "workflow"
            ? ["workflow-folders", targetId]
            : ["run-folders", targetId],
      });
      queryClient.invalidateQueries({ queryKey: ["folders"] });
      queryClient.invalidateQueries({ queryKey: ["folder-summary"] });
      queryClient.invalidateQueries({ queryKey: ["folder-summary-by-workflow"] });
    },
    onError: (err) => toast.error(formatApiError(err)),
  });

  useEffect(() => {
    if (!open) setSearch("");
  }, [open]);

  return (
    <>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>{children}</PopoverTrigger>
        <PopoverContent
          align="start"
          className="w-72 p-0"
          data-testid="add-to-folder-popover"
        >
          <div className="border-b border-slate-200 p-2 space-y-2">
            <div
              className="grid grid-cols-2 overflow-hidden rounded-md border border-slate-200 text-[11px] font-medium"
              role="tablist"
              aria-label="Folder action mode"
              data-testid="add-to-folder-mode-toggle"
            >
              <button
                type="button"
                onClick={() => setMode("add")}
                className={[
                  "px-2 py-1.5",
                  mode === "add"
                    ? "bg-indigo-600 text-white"
                    : "bg-white text-slate-600 hover:bg-slate-50",
                ].join(" ")}
                data-testid="add-to-folder-mode-add"
              >
                Add to folder
              </button>
              <button
                type="button"
                onClick={() => setMode("move")}
                className={[
                  "px-2 py-1.5",
                  mode === "move"
                    ? "bg-indigo-600 text-white"
                    : "bg-white text-slate-600 hover:bg-slate-50",
                ].join(" ")}
                data-testid="add-to-folder-mode-move"
              >
                Move (replace)
              </button>
            </div>
            {mode === "move" && (
              <p
                className="rounded bg-indigo-50 px-2 py-1 text-[10px] leading-tight text-indigo-800"
                data-testid="add-to-folder-move-hint"
              >
                Clicking a folder will remove this {targetKind} from
                every other folder you can modify and place it only
                in the chosen one.
              </p>
            )}
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search folders…"
              className="h-8 text-xs"
              data-testid="add-to-folder-search"
              autoFocus
            />
          </div>
          <div className="max-h-72 overflow-y-auto p-1">
            {foldersQuery.isLoading ? (
              <div className="flex items-center justify-center py-4 text-xs text-slate-500">
                <Loader2 size={12} className="mr-1.5 animate-spin" /> Loading…
              </div>
            ) : filtered.length === 0 ? (
              <p className="px-3 py-4 text-center text-xs text-slate-500" data-testid="add-to-folder-empty">
                {search ? "No matches." : "No folders yet."}
              </p>
            ) : (
              filtered.map((f) => {
                const isMember = memberFolderIds.has(f.folder_id);
                const isBusy = busy.has(f.folder_id);
                const palette = folderColorClasses(f.color);
                const canModify = f.can_modify_items;
                return (
                  <button
                    type="button"
                    key={f.folder_id}
                    disabled={!canModify || isBusy}
                    onClick={() =>
                      toggleMutation.mutate({
                        folderId: f.folder_id,
                        add: !isMember,
                        folderName: f.name,
                      })
                    }
                    className={[
                      "flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-xs",
                      canModify ? "hover:bg-slate-50" : "cursor-not-allowed opacity-60",
                      isMember ? "bg-indigo-50/40" : "",
                    ].join(" ")}
                    data-testid={`add-to-folder-row-${f.folder_id}`}
                  >
                    <span
                      className={["grid h-6 w-6 place-items-center rounded", palette.bg, palette.text].join(" ")}
                    >
                      <FolderIcon name={f.icon} size={12} />
                    </span>
                    <span className="flex-1 truncate font-medium text-slate-800">{f.name}</span>
                    {f.visibility === "org" && (
                      <span className="rounded bg-slate-100 px-1 text-[9px] uppercase text-slate-500">Org</span>
                    )}
                    {isBusy ? (
                      <Loader2 size={12} className="animate-spin text-slate-400" />
                    ) : isMember ? (
                      <Check size={13} className="text-indigo-600" data-testid={`add-to-folder-check-${f.folder_id}`} />
                    ) : (
                      <span className="h-3 w-3" />
                    )}
                  </button>
                );
              })
            )}
          </div>
          <div className="border-t border-slate-200 p-1">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="w-full justify-start gap-1.5 text-xs"
              onClick={() => {
                setOpen(false);
                setCreateOpen(true);
              }}
              data-testid="add-to-folder-new"
            >
              <FolderPlus size={13} className="text-indigo-600" /> New folder…
            </Button>
          </div>
        </PopoverContent>
      </Popover>
      <CreateFolderModal
        open={createOpen}
        onOpenChange={setCreateOpen}
        onCreated={(folder) => {
          queryClient.invalidateQueries({ queryKey: ["folders"] });
          // Auto-add the just-created folder to this target for snappy UX.
          toggleMutation.mutate({
            folderId: folder.folder_id,
            add: true,
            folderName: folder.name,
          });
        }}
      />
    </>
  );
}
